import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "./system/AppGate";

const ROLE_LABELS = {
  admin: "Administración",
  administrative_assistant: "Administración",
  reception: "Ingresos",
  lab_manager: "Jefe de laboratorio",
  technician: "Técnico",
};

function formatTimestamp(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export default function ServiceOrderConversation({
  serviceOrderId,
  legacyNote = "",
  initialSummary,
  onSummaryChange,
  className = "",
}) {
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(
    initialSummary || { messageCount: 0, unreadCount: 0, lastMessageAt: null }
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    setSummary(initialSummary || { messageCount: 0, unreadCount: 0, lastMessageAt: null });
  }, [initialSummary?.messageCount, initialSummary?.unreadCount, initialSummary?.lastMessageAt]);

  const markRead = useCallback(async (rows) => {
    const unread = (rows || []).filter((row) => row.author_id !== user?.id);
    if (!user?.id || !unread.length) return;
    const { error: readError } = await supabase
      .from("service_order_message_reads")
      .upsert(
        unread.map((row) => ({ message_id: row.id, user_id: user.id, read_at: new Date().toISOString() })),
        { onConflict: "message_id,user_id" }
      );
    if (readError) throw readError;
    setSummary((current) => ({ ...current, unreadCount: 0 }));
    onSummaryChange?.({ unreadCount: 0 });
  }, [onSummaryChange, user?.id]);

  const loadMessages = useCallback(async ({ quiet = false } = {}) => {
    if (!serviceOrderId) return;
    if (!quiet) setLoading(true);
    setError("");
    try {
      const { data, error: loadError } = await supabase
        .from("service_order_messages")
        .select("id, service_order_id, author_id, author_name, author_role, body, created_at")
        .eq("service_order_id", serviceOrderId)
        .order("created_at", { ascending: true });
      if (loadError) throw loadError;
      const rows = data || [];
      setMessages(rows);
      const nextSummary = {
        messageCount: rows.length,
        unreadCount: 0,
        lastMessageAt: rows.at(-1)?.created_at || null,
      };
      setSummary(nextSummary);
      onSummaryChange?.(nextSummary);
      await markRead(rows);
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "nearest" }));
    } catch (e) {
      console.error("[ServiceOrderConversation] load", e);
      setError(e?.message || "No se pudieron cargar los mensajes.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [markRead, onSummaryChange, serviceOrderId]);

  useEffect(() => {
    if (open) loadMessages();
  }, [open, loadMessages]);

  useEffect(() => {
    if (!open || !serviceOrderId) return undefined;
    const channel = supabase
      .channel(`os-messages-${serviceOrderId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "service_order_messages",
          filter: `service_order_id=eq.${serviceOrderId}`,
        },
        () => loadMessages({ quiet: true })
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [loadMessages, open, serviceOrderId]);

  const canSend = useMemo(() => draft.trim().length > 0 && draft.trim().length <= 2000, [draft]);

  async function sendMessage() {
    if (!canSend || sending || !serviceOrderId || !user?.id) return;
    setSending(true);
    setError("");
    try {
      const { error: insertError } = await supabase.from("service_order_messages").insert({
        service_order_id: serviceOrderId,
        author_id: user.id,
        author_name: profile?.full_name || user.email || "Usuario",
        author_role: profile?.role || "staff",
        body: draft.trim(),
      });
      if (insertError) throw insertError;
      setDraft("");
      await loadMessages({ quiet: true });
    } catch (e) {
      console.error("[ServiceOrderConversation] send", e);
      setError(e?.message || "No se pudo enviar el mensaje.");
    } finally {
      setSending(false);
    }
  }

  const total = summary?.messageCount || 0;
  const unread = summary?.unreadCount || 0;
  const hasLegacy = String(legacyNote || "").trim().length > 0;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-100 hover:bg-sky-500/20"
        aria-expanded={open}
      >
        <span aria-hidden="true">💬</span>
        <span>Mensajes{total ? ` (${total})` : ""}</span>
        {unread > 0 && (
          <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold text-slate-950">
            {unread} nuevo{unread === 1 ? "" : "s"}
          </span>
        )}
        <span className="text-white/40">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
            {hasLegacy && (
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5 text-xs text-amber-100">
                <div className="mb-1 font-semibold text-amber-300">Nota técnica anterior</div>
                <div className="whitespace-pre-wrap">{legacyNote}</div>
                <div className="mt-1 text-[10px] text-amber-200/60">Antecedente de la ficha del instrumento; sin fecha ni autor registrados.</div>
              </div>
            )}

            {loading && <div className="py-3 text-center text-xs text-white/45">Cargando mensajes…</div>}
            {!loading && !messages.length && !hasLegacy && (
              <div className="py-3 text-center text-xs text-white/45">Todavía no hay mensajes en esta OS.</div>
            )}
            {messages.map((message) => {
              const mine = message.author_id === user?.id;
              return (
                <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] rounded-xl border px-3 py-2 text-xs ${
                    mine
                      ? "border-sky-500/30 bg-sky-500/15 text-sky-50"
                      : "border-white/10 bg-white/5 text-slate-100"
                  }`}>
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 text-[10px] text-white/50">
                      <span className="font-semibold text-white/70">{message.author_name || "Usuario"}</span>
                      <span>{ROLE_LABELS[message.author_role] || message.author_role || "Equipo"}</span>
                      <span>{formatTimestamp(message.created_at)}</span>
                    </div>
                    <div className="whitespace-pre-wrap break-words">{message.body}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {error && <div className="mt-2 text-xs text-rose-300">{error}</div>}

          <div className="mt-3 flex items-end gap-2">
            <textarea
              rows={2}
              maxLength={2000}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Escribí un mensaje para laboratorio o administración…"
              className="min-w-0 flex-1 resize-none rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white placeholder:text-white/30 focus:border-sky-500/50 focus:outline-none"
            />
            <button
              type="button"
              disabled={!canSend || sending}
              onClick={sendMessage}
              className="rounded-lg border border-sky-500/40 bg-sky-500/15 px-3 py-2 text-xs font-semibold text-sky-100 hover:bg-sky-500/25 disabled:opacity-40"
            >
              {sending ? "Enviando…" : "Enviar"}
            </button>
          </div>
          <div className="mt-1 text-[10px] text-white/30">Enter envía · Mayús+Enter agrega una línea</div>
        </div>
      )}
    </div>
  );
}
